import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import CommentHistoryList from "../../../common/components/functional/comments/CommentHistoryList";
import { CommentDisplay } from "../../../common/components/functional/comments/types";
import { TalentComment } from "../../talents/talentAPI";
import {
  canDeleteTalentComment,
  getRelatedStatusDisplayPrefix,
  mapCommentToRemoveCommand,
  mapCommentToUpdateCommand,
} from "./commentHelper";
import {
  removeTalentComment,
  updateTalentComment,
} from "./TalentCommentsSlice";

type Props = {
  comments: TalentComment[];
  currentUserId: string | null;
  previewMode?: boolean;
};

const History = ({
  comments,
  currentUserId,
  previewMode = false,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { talentId } = useParams();

  const displayComments: CommentDisplay[] = comments.map((c) => ({
    id: c.id,
    version: c.version,
    authorId: c.authorId,
    authorName: `${c.authorFirstName} ${c.authorLastName}`,
    dateTime: c.dateTime,
    comment: c.comment,
    displayPrefix: getRelatedStatusDisplayPrefix(c.relatedStatus, t),
    canDelete: canDeleteTalentComment(c.relatedStatus),
  }));

  const handleEdit = async (id: string, newText: string): Promise<boolean> => {
    const comment = comments.find((c) => c.id === id);
    if (!comment) return false;
    const action = await dispatch(
      updateTalentComment(mapCommentToUpdateCommand(comment, talentId, newText)),
    );
    if (isFulfilled(action)) {
      return true;
    }
    return false;
  };

  const handleDelete = async (id: string): Promise<boolean> => {
    const comment = comments.find((c) => c.id === id);
    if (!comment || !canDeleteTalentComment(comment.relatedStatus)) return false;
    const action = await dispatch(
      removeTalentComment(mapCommentToRemoveCommand(comment)),
    );
    if (isFulfilled(action)) {
      return true;
    }
    return false;
  };

  return (
    <CommentHistoryList
      comments={displayComments}
      currentUserId={previewMode ? null : currentUserId}
      onEdit={handleEdit}
      onDelete={handleDelete}
      deleteConfirmationText={t("talentProfile:deleteCommentApproval")}
    />
  );
};

export default History;
